import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';

/**
 * Kampanya İade Hesaplama Komponenti
 * İade talebi oluşturulurken kampanya etkilerini hesaplar ve gösterir
 */
const CampaignRefundCalculation = ({ order, selectedItems, onCalculationComplete }) => {
    const [calculationData, setCalculationData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { token } = useAuth();

    // Kampanya türü görüntü isimlerini döndür
    const getCampaignTypeDisplayName = (type) => {
        const displayNames = {
            'flash_sale': 'Flash Sale',
            'category': 'Kategori İndirimi',
            'cart': 'Sepet Kampanyası',
            'coupon': 'Kupon İndirimi',
            'package': 'Paket Kampanyası',
            'special_day': 'Özel Gün Kampanyası'
        };
        return displayNames[type] || type;
    };

    // Kampanya badge rengi döndür
    const getCampaignBadgeColor = (type) => {
        const colors = {
            'flash_sale': 'bg-red-100 text-red-800',
            'category': 'bg-blue-100 text-blue-800',
            'cart': 'bg-green-100 text-green-800',
            'coupon': 'bg-purple-100 text-purple-800',
            'package': 'bg-yellow-100 text-yellow-800',
            'special_day': 'bg-pink-100 text-pink-800'
        };
        return colors[type] || 'bg-gray-100 text-gray-800';
    };

    // Kampanya türünü tespit et
    const detectCampaignType = (order) => {
        // Doğrudan belirtilen kampanya türü varsa kullan
        if (order.campaign_type) return order.campaign_type;
        
        // Campaigns dizisinden ilk kampanya türünü al
        if (order.campaigns && order.campaigns.length > 0) {
            return order.campaigns[0].campaign_type;
        }
        
        // İndirim alanlarına göre türü belirle
        if (order.package_discount > 0) return 'package';
        if (order.coupon_discount > 0) return 'coupon';
        if (order.category_discount > 0) return 'category';
        if (order.cart_discount > 0) return 'cart';
        if (order.flash_sale_discount > 0) return 'flash_sale';
        if (order.special_day_discount > 0) return 'special_day';
        
        return null;
    };

    // Kupon kampanyası için iade hesaplama
    const calculateCouponCampaignRefund = (items, order) => {
        // DÜZELTME: Gerçek ödenen tutarı kullan
        const orderTotal = order.total_price; // Gerçek ödenen tutar (kampanya indirimleri dahil)
        
        // İade tutarı = Gerçek ödenen tutar (kampanya indirimleri zaten dahil)
        const correctedRefund = orderTotal;
        
        // Bilgi amaçlı: orijinal fiyat hesaplama
        const normalTotal = items.reduce((sum, item) => sum + (parseFloat(item.price) * item.quantity), 0);
        const couponDiscount = normalTotal - orderTotal;
        
        console.log('DÜZELTME - Kupon kampanyası hesaplama:', {
            normalTotal,
            orderTotal,
            couponDiscount,
            correctedRefund,
            message: 'Gerçek ödenen tutar iade ediliyor (kampanya indirimleri dahil)'
        });
        
        return {
            isCouponCampaign: true,
            originalTotal: normalTotal,
            couponDiscount: couponDiscount,
            refundedDiscount: 0, // Kampanya indirimi zaten dahil
            correctedRefund: correctedRefund, // Gerçek ödenen tutar
            refundRatio: 1 // Tam iade
        };
    };
    
    // DÜZELTME: Kategori kampanyası için iade hesaplama
    const calculateCategoryCampaignRefund = (items, order) => {
        // DOĞRU MANTIK: Gerçek ödenen tutarı kullan
        const orderTotal = order.total_price; // Gerçek ödenen tutar (kampanya indirimleri dahil)
        
        // İade tutarı = Gerçek ödenen tutar (kampanya indirimleri zaten dahil)
        const correctedRefund = orderTotal;
        
        // Bilgi amaçlı: orijinal fiyat hesaplama
        const normalTotal = items.reduce((sum, item) => sum + (parseFloat(item.price) * item.quantity), 0);
        const categoryDiscount = normalTotal - orderTotal;
        
        // İade edilen ürünlerin oranını hesapla (bilgi amaçlı)
        const refundRatio = 1; // Tam iade
        
        // İade edilecek kategori indirimi (artık sıfır, çünkü gerçek ödenen tutarı iade ediyoruz)
        const refundedDiscount = 0;
        
        return {
            isCategoryCampaign: true,
            originalTotal: normalTotal,
            categoryDiscount: categoryDiscount,
            refundedDiscount: refundedDiscount,
            correctedRefund: correctedRefund,
            refundRatio: refundRatio
        };
    };
    
    // DÜZELTME: Sepet kampanyası için iade hesaplama
    const calculateCartCampaignRefund = (items, order) => {
        // DOĞRU MANTIK: Gerçek ödenen tutarı kullan
        const orderTotal = order.total_price; // Gerçek ödenen tutar (kampanya indirimleri dahil)
        
        // İade tutarı = Gerçek ödenen tutar (kampanya indirimleri zaten dahil)
        const correctedRefund = orderTotal;
        
        // Bilgi amaçlı: orijinal fiyat hesaplama
        const normalTotal = items.reduce((sum, item) => sum + (parseFloat(item.price) * item.quantity), 0);
        const cartDiscount = normalTotal - orderTotal;
        
        console.log('DÜZELTME - Sepet kampanyası hesaplama:', {
            normalTotal,
            orderTotal,
            cartDiscount,
            correctedRefund,
            message: 'Gerçek ödenen tutar iade ediliyor (kampanya indirimleri dahil)'
        });
        
        return {
            isCartCampaign: true,
            originalTotal: normalTotal,
            cartDiscount: cartDiscount,
            refundedDiscount: 0, // Kampanya indirimi zaten dahil
            correctedRefund: correctedRefund, // Gerçek ödenen tutar
            refundRatio: 1 // Tam iade
        };
    };
    
    // DÜZELTME: Flash Sale kampanyası için iade hesaplama
    const calculateFlashSaleCampaignRefund = (items, order) => {
        // DOĞRU MANTIK: Gerçek ödenen tutarı kullan
        const orderTotal = order.total_price; // Gerçek ödenen tutar (kampanya indirimleri dahil)
        
        // İade tutarı = Gerçek ödenen tutar (kampanya indirimleri zaten dahil)
        const correctedRefund = orderTotal;
        
        // Bilgi amaçlı: orijinal fiyat hesaplama
        const normalTotal = items.reduce((sum, item) => sum + (parseFloat(item.price) * item.quantity), 0);
        const flashSaleDiscount = normalTotal - orderTotal;
        
        console.log('DÜZELTME - Flash Sale hesaplama:', {
            normalTotal,
            orderTotal,
            flashSaleDiscount,
            correctedRefund,
            message: 'Gerçek ödenen tutar iade ediliyor (kampanya indirimleri dahil)'
        });
        
        return {
            isFlashSaleCampaign: true,
            originalTotal: normalTotal,
            flashSaleDiscount: flashSaleDiscount,
            refundedDiscount: 0, // Kampanya indirimi zaten dahil
            correctedRefund: correctedRefund, // Gerçek ödenen tutar
            refundRatio: 1 // Tam iade
        };
    };
    
    // Özel gün kampanyası için iade hesaplama
    const calculateSpecialDayCampaignRefund = (items, order) => {
        // Normal fiyat toplamını hesapla
        const normalTotal = items.reduce((sum, item) => sum + (parseFloat(item.price) * item.quantity), 0);
        
        // Siparişteki tüm ürünlerin toplamını bulma
        const orderTotal = order.total_price || order.order_items?.reduce((sum, item) => sum + (parseFloat(item.price_at_purchase) * item.quantity), 0) || 0;
        
        // Özel gün indirimi
        const specialDayDiscount = order.special_day_discount || 0;
        
        // İade edilen ürünlerin oranını hesapla
        const refundRatio = normalTotal / orderTotal;
        
        // İade edilecek özel gün indirimi
        const refundedDiscount = specialDayDiscount * refundRatio;
        
        // Düzeltilmiş iade tutarı
        const correctedRefund = normalTotal - refundedDiscount;
        
        return {
            isSpecialDayCampaign: true,
            originalTotal: normalTotal,
            specialDayDiscount: specialDayDiscount,
            refundedDiscount: refundedDiscount,
            correctedRefund: correctedRefund,
            refundRatio: refundRatio
        };
    };

    // Paket kampanyası (2 al 1 öde) için iade tutarını düzelt
    const calculatePackageCampaignRefund = (items, order) => {
        // Toplam iade edilecek ürün sayısını hesapla
        const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
        
        // Normal fiyat toplamını hesapla
        const normalTotal = items.reduce((sum, item) => sum + (parseFloat(item.price) * item.quantity), 0);
        
        // Paket kampanyası bilgilerini al
        const campaignInfo = order.campaigns?.find(c => c.campaign_type === 'package' || c.campaign_type === 'bundle') || 
                            ((order.campaign_type === 'package' || order.campaign_type === 'bundle') ? { campaign_details: order.campaign_details } : null) ||
                            (order.package_discount > 0 ? { is_package_discount: true } : null);
        
        // Siparişteki toplam ürün sayısı
        const orderTotalItems = order.order_items?.reduce((sum, item) => sum + item.quantity, 0) || 0;
        
        // Paketteki kalan ürün sayısı (iade sonrası)
        const remainingItems = orderTotalItems - totalItems;
        
        console.log('Paket kampanyası detayları:', { totalItems, remainingItems, orderTotalItems, normalTotal });
        
        // İade edilen ürünler tüm paketi bozuyor mu kontrol et
        if (campaignInfo && campaignInfo.campaign_details) {
            // "2 al 1 öde" kampanyası için hesaplama
            if (campaignInfo.campaign_details && (campaignInfo.campaign_details.includes('2 al 1 öde') || 
                campaignInfo.campaign_details.includes('2 al 1 öde') ||
                campaignInfo.campaign_details.includes('2 for 1'))) {
                
                // Önemli: 2 al 1 öde kampanyasında paket bozulup bozulmadığına göre farklı hesaplama yap
                // Eğer kalan ürün sayısı 1'e düşütü veya hiç ürün kalmadıysa paket bozulmuştur
                if (remainingItems <= 1) {
                    // Paket bozuldu - müşteri gerçekten ödediği tutarı almalı
                    console.log('Paket bozuldu, iade tutarı müşterinin ödediği tutar olacak');
                    
                    // Örnek: 2 ürün için 1 ürün fiyatı ödendi (119.90 TL), 1 iade edilirse 119.90/2 = 59.95 TL iade edilmeli
                    const originalPackageDiscount = normalTotal / 2; // 2 al 1 öde indirimi
                    const packageTotal = order.total_price - order.package_discount;
                    
                    // Ürün başına ödenen ortalama fiyat
                    const pricePerItem = packageTotal / orderTotalItems;
                    
                    // İade edilecek tutar
                    const correctedRefund = totalItems * pricePerItem;
                    
                    return {
                        isPackageCampaign: true,
                        originalTotal: normalTotal,
                        correctedRefund: correctedRefund,
                        packageBroken: true,
                        packageGroups: Math.floor(orderTotalItems / 2),
                        remainingItems: remainingItems,
                        pricePerItem: pricePerItem
                    };
                } else {
                    // Paket hala geçerli - kampanya koşulları devam ediyor, ama ürünlerin bir kısmı iade ediliyor
                    console.log('Paket hala geçerli, iade tutarı normal fiyatın tamamı olacak');
                    
                    // İşletme zarar etmemeli, bu durumda normal fiyatı iade edilmeli
                    // Çünkü müşteri hala kampanya avantajını kullanıyor
                    return {
                        isPackageCampaign: true,
                        originalTotal: normalTotal,
                        correctedRefund: normalTotal,
                        packageBroken: false,
                        remainingItems: remainingItems
                    };
                }
            }
        }
        
        // Paket türü belirlenemiyorsa, varsayılan davranış
        // Varsayılan olarak normal ürün fiyatını iade et
        console.log('Paket türü belirlenemedi, normal fiyat iade edilecek:', normalTotal);
        return {
            isPackageCampaign: true,
            originalTotal: normalTotal,
            correctedRefund: normalTotal,
            packageBroken: false
        };
    };

    // Kampanya iade hesaplamasını yap
    const calculateCampaignRefund = async () => {
        if (!selectedItems || selectedItems.length === 0) {
            setCalculationData(null);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            // Siparişte kampanya var mı kontrol et
            const hasCampaign = order.campaign_discount && order.campaign_discount > 0;
            
            // Kampanya türünü tespit et
            const campaignType = detectCampaignType(order);
            console.log('Tespit edilen kampanya türü:', campaignType);
            
            // Paket kampanyası (2 al 1 öde) olup olmadığını kontrol et
            const isPackageCampaign = campaignType === 'package' || campaignType === 'bundle' || order.package_discount > 0;
            
            // Kupon kampanyası olup olmadığını kontrol et
            const isCouponCampaign = campaignType === 'coupon' || order.coupon_discount > 0;
            
            // Kategori kampanyası olup olmadığını kontrol et
            const isCategoryCampaign = campaignType === 'category' || order.category_discount > 0;
            
            // Sepet kampanyası olup olmadığını kontrol et
            const isCartCampaign = campaignType === 'cart' || order.cart_discount > 0;
            
            // Flash Sale kampanyası olup olmadığını kontrol et
            const isFlashSaleCampaign = campaignType === 'flash_sale' || order.flash_sale_discount > 0;
            
            // Özel gün kampanyası olup olmadığını kontrol et
            const isSpecialDayCampaign = campaignType === 'special_day' || order.special_day_discount > 0;
            
            // Kampanya türüne göre hesaplama yap
            if (isPackageCampaign) {
                const packageCalculation = calculatePackageCampaignRefund(selectedItems, order);
                
                // Debug için siparişi logla
                console.log('Paket kampanyası tespit edildi. Sipariş bilgileri:', order);
                console.log('Paket kampanyası hesaplama sonucu:', packageCalculation);
                
                setCalculationData({
                    originalOrder: { 
                        id: order.id,
                        total_price: order.total_price,
                        campaign_discount: order.campaign_discount || 0,
                        campaign_type: 'package'
                    },
                    refundCalculations: {
                        totalRefundAmount: packageCalculation.correctedRefund,
                        campaignImpact: [{
                            campaign_type: 'package',
                            campaign_name: '2 Al 1 Öde Kampanyası',
                            originalDiscount: packageCalculation.originalTotal - packageCalculation.correctedRefund,
                            refundedAmount: packageCalculation.correctedRefund
                        }]
                    },
                    estimatedRefundAmount: packageCalculation.correctedRefund,
                    isPackageCampaign: true,
                    packageCalculation: packageCalculation
                });
                
                if (onCalculationComplete) {
                    // Hesaplama tamamlandığında callbacke bilgisini aktar
                    onCalculationComplete({
                        estimatedRefundAmount: packageCalculation.correctedRefund,
                        isPackageCampaign: true
                    });
                }
                setLoading(false);
                return;
            } else if (isCouponCampaign) {
                const couponCalculation = calculateCouponCampaignRefund(selectedItems, order);
                
                console.log('Kupon kampanyası tespit edildi. Sipariş bilgileri:', order);
                console.log('Kupon kampanyası hesaplama sonucu:', couponCalculation);
                
                setCalculationData({
                    originalOrder: { 
                        id: order.id,
                        total_price: order.total_price,
                        campaign_discount: order.campaign_discount || 0,
                        campaign_type: 'coupon'
                    },
                    refundCalculations: {
                        totalRefundAmount: couponCalculation.originalTotal,
                        campaignImpact: [{
                            campaign_type: 'coupon',
                            campaign_name: 'Kupon İndirimi',
                            originalDiscount: couponCalculation.couponDiscount,
                            refundedAmount: couponCalculation.refundedDiscount
                        }]
                    },
                    estimatedRefundAmount: couponCalculation.correctedRefund,
                    isCouponCampaign: true,
                    couponCalculation: couponCalculation
                });
                
                if (onCalculationComplete) {
                    onCalculationComplete({
                        estimatedRefundAmount: couponCalculation.correctedRefund,
                        isCouponCampaign: true
                    });
                }
                setLoading(false);
                return;
            } else if (isCategoryCampaign) {
                const categoryCalculation = calculateCategoryCampaignRefund(selectedItems, order);
                
                console.log('Kategori kampanyası tespit edildi. Sipariş bilgileri:', order);
                console.log('Kategori kampanyası hesaplama sonucu:', categoryCalculation);
                
                setCalculationData({
                    originalOrder: { 
                        id: order.id,
                        total_price: order.total_price,
                        campaign_discount: order.campaign_discount || 0,
                        campaign_type: 'category'
                    },
                    refundCalculations: {
                        totalRefundAmount: categoryCalculation.originalTotal,
                        campaignImpact: [{
                            campaign_type: 'category',
                            campaign_name: 'Kategori İndirimi',
                            originalDiscount: categoryCalculation.categoryDiscount,
                            refundedAmount: categoryCalculation.refundedDiscount
                        }]
                    },
                    estimatedRefundAmount: categoryCalculation.correctedRefund,
                    isCategoryCampaign: true,
                    categoryCalculation: categoryCalculation
                });
                
                if (onCalculationComplete) {
                    onCalculationComplete({
                        estimatedRefundAmount: categoryCalculation.correctedRefund,
                        isCategoryCampaign: true
                    });
                }
                setLoading(false);
                return;
            } else if (isCartCampaign) {
                const cartCalculation = calculateCartCampaignRefund(selectedItems, order);
                
                console.log('Sepet kampanyası tespit edildi. Sipariş bilgileri:', order);
                console.log('Sepet kampanyası hesaplama sonucu:', cartCalculation);
                
                setCalculationData({
                    originalOrder: { 
                        id: order.id,
                        total_price: order.total_price,
                        campaign_discount: order.campaign_discount || 0,
                        campaign_type: 'cart'
                    },
                    refundCalculations: {
                        totalRefundAmount: cartCalculation.originalTotal,
                        campaignImpact: [{
                            campaign_type: 'cart',
                            campaign_name: 'Sepet İndirimi',
                            originalDiscount: cartCalculation.cartDiscount,
                            refundedAmount: cartCalculation.refundedDiscount
                        }]
                    },
                    estimatedRefundAmount: cartCalculation.correctedRefund,
                    isCartCampaign: true,
                    cartCalculation: cartCalculation
                });
                
                if (onCalculationComplete) {
                    onCalculationComplete({
                        estimatedRefundAmount: cartCalculation.correctedRefund,
                        isCartCampaign: true
                    });
                }
                setLoading(false);
                return;
            } else if (isFlashSaleCampaign) {
                const flashSaleCalculation = calculateFlashSaleCampaignRefund(selectedItems, order);
                
                console.log('Flash Sale kampanyası tespit edildi. Sipariş bilgileri:', order);
                console.log('Flash Sale kampanyası hesaplama sonucu:', flashSaleCalculation);
                
                setCalculationData({
                    originalOrder: { 
                        id: order.id,
                        total_price: order.total_price,
                        campaign_discount: order.campaign_discount || 0,
                        campaign_type: 'flash_sale'
                    },
                    refundCalculations: {
                        totalRefundAmount: flashSaleCalculation.originalTotal,
                        campaignImpact: [{
                            campaign_type: 'flash_sale',
                            campaign_name: 'Flash Sale İndirimi',
                            originalDiscount: flashSaleCalculation.flashSaleDiscount,
                            refundedAmount: flashSaleCalculation.flashSaleDiscount
                        }]
                    },
                    estimatedRefundAmount: flashSaleCalculation.correctedRefund,
                    isFlashSaleCampaign: true,
                    flashSaleCalculation: flashSaleCalculation
                });
                
                if (onCalculationComplete) {
                    onCalculationComplete({
                        estimatedRefundAmount: flashSaleCalculation.correctedRefund,
                        isFlashSaleCampaign: true
                    });
                }
                setLoading(false);
                return;
            } else if (isSpecialDayCampaign) {
                const specialDayCalculation = calculateSpecialDayCampaignRefund(selectedItems, order);
                
                console.log('Özel Gün kampanyası tespit edildi. Sipariş bilgileri:', order);
                console.log('Özel Gün kampanyası hesaplama sonucu:', specialDayCalculation);
                
                setCalculationData({
                    originalOrder: { 
                        id: order.id,
                        total_price: order.total_price,
                        campaign_discount: order.campaign_discount || 0,
                        campaign_type: 'special_day'
                    },
                    refundCalculations: {
                        totalRefundAmount: specialDayCalculation.originalTotal,
                        campaignImpact: [{
                            campaign_type: 'special_day',
                            campaign_name: 'Özel Gün İndirimi',
                            originalDiscount: specialDayCalculation.specialDayDiscount,
                            refundedAmount: specialDayCalculation.refundedDiscount
                        }]
                    },
                    estimatedRefundAmount: specialDayCalculation.correctedRefund,
                    isSpecialDayCampaign: true,
                    specialDayCalculation: specialDayCalculation
                });
                
                if (onCalculationComplete) {
                    onCalculationComplete({
                        estimatedRefundAmount: specialDayCalculation.correctedRefund,
                        isSpecialDayCampaign: true
                    });
                }
                setLoading(false);
                return;
            }
            
            // API çağrısı için genel hesaplama
            // Normal hesaplama yap, kampanya yoksa
            let totalRefundAmount = selectedItems.reduce((sum, item) => sum + (parseFloat(item.price) * item.quantity), 0);
            
            if (!hasCampaign) {
                // Kampanya yoksa hesaplama yapma, direkt ürün tutarını göster
                console.log('Kampanya tespit edilmedi, normal iade hesaplaması yapılıyor.');
                setCalculationData({
                    originalOrder: { 
                        id: order.id,
                        total_price: order.total_price,
                        campaign_discount: 0,
                        campaign_type: null
                    },
                    refundCalculations: {
                        totalRefundAmount: totalRefundAmount,
                        campaignImpact: null
                    },
                    estimatedRefundAmount: totalRefundAmount
                });
                
                if (onCalculationComplete) {
                    onCalculationComplete({
                        estimatedRefundAmount: totalRefundAmount
                    });
                }
                setLoading(false);
                return;
            }
            
            const refundItems = selectedItems.map(item => ({
                productId: item.product_id,
                variantId: item.variant_id,
                quantity: item.quantity,
                refundAmount: parseFloat(item.price) * item.quantity
            }));

            // API yolunu doğru şekilde kullan
            const response = await api.post('/api/user-campaign-tracking/calculate-campaign-refund', {
                orderId: order.id,
                refundItems: refundItems
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data && response.data.success) {
                setCalculationData(response.data.data);
                if (onCalculationComplete) {
                    onCalculationComplete(response.data.data);
                }
            } else {
                throw new Error(response.data?.error || 'Kampanya hesaplama yapılamadı');
            }
        } catch (error) {
            console.error('Kampanya iade hesaplama hatası:', error);
            // Eğer kampanya yok ise hata gösterme, sadece bilgi ver
            if (error.response?.status === 404) {
                console.log('Kampanya hesaplama API bulunamadı, yerel hesaplama yapılıyor');
                
                // Kampanya türünü tespit et ve ilgili hesaplamayı yap
                const campaignType = detectCampaignType(order);
                console.log('Hata durumunda tespit edilen kampanya türü:', campaignType);
                
                // Paket kampanyası kontrolü
                const isPackageCampaign = campaignType === 'package' || campaignType === 'bundle' || order.package_discount > 0;
                // Kupon kampanyası kontrolü
                const isCouponCampaign = campaignType === 'coupon' || order.coupon_discount > 0;
                // Kategori kampanyası kontrolü
                const isCategoryCampaign = campaignType === 'category' || order.category_discount > 0;
                // Sepet kampanyası kontrolü
                const isCartCampaign = campaignType === 'cart' || order.cart_discount > 0;
                // Flash Sale kampanyası kontrolü
                const isFlashSaleCampaign = campaignType === 'flash_sale' || order.flash_sale_discount > 0;
                // Özel gün kampanyası kontrolü
                const isSpecialDayCampaign = campaignType === 'special_day' || order.special_day_discount > 0;
                
                if (isPackageCampaign) {
                    const packageCalculation = calculatePackageCampaignRefund(selectedItems, order);
                    console.log('Hata durumunda paket kampanyası hesaplama sonucu:', packageCalculation);
                    
                    setCalculationData({
                        originalOrder: { 
                            id: order.id,
                            total_price: order.total_price,
                            campaign_discount: order.campaign_discount || 0,
                            campaign_type: 'package'
                        },
                        refundCalculations: {
                            totalRefundAmount: packageCalculation.correctedRefund,
                            campaignImpact: [{
                                campaign_type: 'package',
                                campaign_name: '2 Al 1 Öde Kampanyası',
                                originalDiscount: packageCalculation.originalTotal - packageCalculation.correctedRefund,
                                refundedAmount: packageCalculation.refundedDiscount || packageCalculation.correctedRefund
                            }]
                        },
                        estimatedRefundAmount: packageCalculation.correctedRefund,
                        isPackageCampaign: true,
                        packageCalculation: packageCalculation
                    });
                    setLoading(false);
                    return;
                } else if (isCouponCampaign) {
                    const couponCalculation = calculateCouponCampaignRefund(selectedItems, order);
                    console.log('Hata durumunda kupon kampanyası hesaplama sonucu:', couponCalculation);
                    
                    setCalculationData({
                        originalOrder: { 
                            id: order.id,
                            total_price: order.total_price,
                            campaign_discount: order.campaign_discount || 0,
                            campaign_type: 'coupon'
                        },
                        refundCalculations: {
                            totalRefundAmount: couponCalculation.originalTotal,
                            campaignImpact: [{
                                campaign_type: 'coupon',
                                campaign_name: 'Kupon İndirimi',
                                originalDiscount: couponCalculation.couponDiscount,
                                refundedAmount: couponCalculation.refundedDiscount
                            }]
                        },
                        estimatedRefundAmount: couponCalculation.correctedRefund,
                        isCouponCampaign: true,
                        couponCalculation: couponCalculation
                    });
                    setLoading(false);
                    return;
                } else if (isCategoryCampaign) {
                    const categoryCalculation = calculateCategoryCampaignRefund(selectedItems, order);
                    console.log('Hata durumunda kategori kampanyası hesaplama sonucu:', categoryCalculation);
                    
                    setCalculationData({
                        originalOrder: { 
                            id: order.id,
                            total_price: order.total_price,
                            campaign_discount: order.campaign_discount || 0,
                            campaign_type: 'category'
                        },
                        refundCalculations: {
                            totalRefundAmount: categoryCalculation.originalTotal,
                            campaignImpact: [{
                                campaign_type: 'category',
                                campaign_name: 'Kategori İndirimi',
                                originalDiscount: categoryCalculation.categoryDiscount,
                                refundedAmount: categoryCalculation.refundedDiscount
                            }]
                        },
                        estimatedRefundAmount: categoryCalculation.correctedRefund,
                        isCategoryCampaign: true,
                        categoryCalculation: categoryCalculation
                    });
                    setLoading(false);
                    return;
                } else if (isCartCampaign) {
                    const cartCalculation = calculateCartCampaignRefund(selectedItems, order);
                    console.log('Hata durumunda sepet kampanyası hesaplama sonucu:', cartCalculation);
                    
                    setCalculationData({
                        originalOrder: { 
                            id: order.id,
                            total_price: order.total_price,
                            campaign_discount: order.campaign_discount || 0,
                            campaign_type: 'cart'
                        },
                        refundCalculations: {
                            totalRefundAmount: cartCalculation.originalTotal,
                            campaignImpact: [{
                                campaign_type: 'cart',
                                campaign_name: 'Sepet İndirimi',
                                originalDiscount: cartCalculation.cartDiscount,
                                refundedAmount: cartCalculation.refundedDiscount
                            }]
                        },
                        estimatedRefundAmount: cartCalculation.correctedRefund,
                        isCartCampaign: true,
                        cartCalculation: cartCalculation
                    });
                    setLoading(false);
                    return;
                } else if (isFlashSaleCampaign) {
                    const flashSaleCalculation = calculateFlashSaleCampaignRefund(selectedItems, order);
                    console.log('Hata durumunda flash sale kampanyası hesaplama sonucu:', flashSaleCalculation);
                    
                    setCalculationData({
                        originalOrder: { 
                            id: order.id,
                            total_price: order.total_price,
                            campaign_discount: order.campaign_discount || 0,
                            campaign_type: 'flash_sale'
                        },
                        refundCalculations: {
                            totalRefundAmount: flashSaleCalculation.originalTotal,
                            campaignImpact: [{
                                campaign_type: 'flash_sale',
                                campaign_name: 'Flash Sale İndirimi',
                                originalDiscount: flashSaleCalculation.flashSaleDiscount,
                                refundedAmount: flashSaleCalculation.refundedDiscount
                            }]
                        },
                        estimatedRefundAmount: flashSaleCalculation.correctedRefund,
                        isFlashSaleCampaign: true,
                        flashSaleCalculation: flashSaleCalculation
                    });
                    setLoading(false);
                    return;
                } else if (isSpecialDayCampaign) {
                    const specialDayCalculation = calculateSpecialDayCampaignRefund(selectedItems, order);
                    console.log('Hata durumunda özel gün kampanyası hesaplama sonucu:', specialDayCalculation);
                    
                    setCalculationData({
                        originalOrder: { 
                            id: order.id,
                            total_price: order.total_price,
                            campaign_discount: order.campaign_discount || 0,
                            campaign_type: 'special_day'
                        },
                        refundCalculations: {
                            totalRefundAmount: specialDayCalculation.originalTotal,
                            campaignImpact: [{
                                campaign_type: 'special_day',
                                campaign_name: 'Özel Gün İndirimi',
                                originalDiscount: specialDayCalculation.specialDayDiscount,
                                refundedAmount: specialDayCalculation.refundedDiscount
                            }]
                        },
                        estimatedRefundAmount: specialDayCalculation.correctedRefund,
                        isSpecialDayCampaign: true,
                        specialDayCalculation: specialDayCalculation
                    });
                    setLoading(false);
                    return;
                }
                
                // Kampanya tespit edilemediğinde normal iade hesapla
                let totalRefundAmount = selectedItems.reduce((sum, item) => sum + (parseFloat(item.price) * item.quantity), 0);
                console.log('Kampanya tespit edilmedi, normal iade tutarı hesaplanıyor:', totalRefundAmount);
                
                setCalculationData({
                    originalOrder: { 
                        id: order.id,
                        total_price: order.total_price,
                        campaign_discount: 0,
                        campaign_type: null
                    },
                    refundCalculations: {
                        totalRefundAmount: totalRefundAmount,
                        campaignImpact: null
                    },
                    estimatedRefundAmount: totalRefundAmount
                });
                setError(null);
            } else {
                setError('Kampanya hesaplama sırasında hata oluştu: ' + (error.message || 'Bilinmeyen hata'));
            }
        } finally {
            setLoading(false);
        }
    };

    // Seçilen ürünler değiştiğinde hesaplamayı yenile
    useEffect(() => {
        calculateCampaignRefund();
    }, [selectedItems, order.id]);

    if (loading) {
        return (
            <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="bg-gray-50 rounded-xl p-6"
            >
                <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-green mr-3"></div>
                    <span className="text-gray-600">Kampanya etkileri hesaplanıyor...</span>
                </div>
            </motion.div>
        );
    }

    if (error) {
        return (
            <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="bg-red-50 border border-red-200 rounded-xl p-4"
            >
                <div className="flex items-center">
                    <div className="w-8 h-8 bg-red-500 rounded-lg flex items-center justify-center mr-3">
                        <span className="text-white text-sm">⚠️</span>
                    </div>
                    <div>
                        <p className="font-semibold text-red-800">Kampanya Hesaplama Hatası</p>
                        <p className="text-sm text-red-600">{error}</p>
                    </div>
                </div>
                <button 
                    onClick={calculateCampaignRefund}
                    className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700"
                >
                    Tekrar Dene
                </button>
            </motion.div>
        );
    }

    if (!calculationData) {
        return null;
    }
    
    // Siparişte kampanya kullanılmış mı kontrol et
    const orderHasCampaign = calculationData.originalOrder && 
                           calculationData.originalOrder.campaign_discount && 
                           calculationData.originalOrder.campaign_discount > 0;
                           
    // Paket kampanyası olup olmadığını kontrol et (2 al 1 öde gibi)
    const isPackageCampaign = calculationData.isPackageCampaign || 
                             (calculationData.originalOrder && 
                             (calculationData.originalOrder.campaign_type === 'package' || calculationData.originalOrder.campaign_type === 'bundle' ||
                              (order.campaigns && order.campaigns.some(c => c.campaign_type === 'package' || c.campaign_type === 'bundle')))) ||
                              order.package_discount > 0;
                             
    // Flash Sale kampanyası kontrolü
    const isFlashSaleCampaign = calculationData.isFlashSaleCampaign || 
                               (calculationData.originalOrder && calculationData.originalOrder.campaign_type === 'flash_sale') ||
                               order.flash_sale_discount > 0;
                               
    // Kupon kampanyası kontrolü
    const isCouponCampaign = calculationData.isCouponCampaign || 
                            (calculationData.originalOrder && calculationData.originalOrder.campaign_type === 'coupon') ||
                            order.coupon_discount > 0;

    const { refundCalculations } = calculationData;
    const hasCampaignImpact = refundCalculations.campaignImpact && refundCalculations.campaignImpact.length > 0;

    return (
        <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-br from-blue-50 to-purple-50 border-2 border-blue-200 rounded-xl p-6 mb-6"
        >
            <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl flex items-center justify-center mr-3">
                    <span className="text-white text-lg">💰</span>
                </div>
                <div>
                    <h4 className="font-bold text-blue-800">İade Tutarı{orderHasCampaign && " ve Kampanya Etkileri"}</h4>
                    <p className="text-sm text-blue-600">
                        {orderHasCampaign ? "Kampanya indirimlerinin iade üzerindeki etkisi" : "İade edilecek tahmini tutar"}                    
                    </p>
                </div>
            </div>

            {/* Kampanya türlerine göre özel gösterim */}
            {isPackageCampaign ? (
                <div className="space-y-4">
                    <div className="bg-white rounded-lg p-4 border border-yellow-300">
                        <div className="text-center">
                            <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-3">
                                <span className="text-2xl">🎟️</span>
                            </div>
                            <h5 className="font-semibold text-gray-800 mb-2">Paket Kampanyası İadesi</h5>
                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4 text-left">
                                {calculationData.packageCalculation?.packageBroken ? (
                                    <p className="text-sm text-yellow-800">
                                        <span className="font-semibold">2 Al 1 Öde</span> kampanyasında paket bozulacak şekilde iade yapıyorsunuz.
                                        Ürünler için ödediğiniz ortalama tutar iade edilecektir.
                                    </p>
                                ) : (
                                    <p className="text-sm text-yellow-800">
                                        <span className="font-semibold">2 Al 1 Öde</span> kampanyası uygulanmış bir siparişten iade yapıyorsunuz.
                                        Kampanya koşulları bozulmadığı için ürünün normal fiyatı iade edilecektir.
                                    </p>
                                )}
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="bg-blue-50 rounded-lg p-3">
                                    <p className="text-sm text-gray-600">Katalog Fiyatı</p>
                                    <p className="text-xl font-bold text-blue-600">
                                        ₺{calculationData.packageCalculation?.originalTotal?.toFixed(2) || '0.00'}
                                    </p>
                                </div>
                                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                                    <p className="text-sm text-gray-600">İade Tutarı</p>
                                    <p className="text-2xl font-bold text-green-600">
                                        ₺{calculationData.estimatedRefundAmount?.toFixed(2) || '0.00'}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ) : isFlashSaleCampaign ? (
                <div className="space-y-4">
                    <div className="bg-white rounded-lg p-4 border border-red-300">
                        <div className="text-center">
                            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
                                <span className="text-2xl">⚡️</span>
                            </div>
                            <h5 className="font-semibold text-gray-800 mb-2">Flash Sale İadesi</h5>
                            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 text-left">
                                <p className="text-sm text-red-800">
                                    <span className="font-semibold">Flash Sale</span> kampanyası ile indirimli olarak satın aldığınız ürünü iade ediyorsunuz.
                                    Ürün için ödediğiniz indirimli tutar iade edilecektir.
                                </p>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="bg-blue-50 rounded-lg p-3">
                                    <p className="text-sm text-gray-600">Normal Fiyat</p>
                                    <p className="text-xl font-bold text-blue-600">
                                        ₺{calculationData.flashSaleCalculation?.originalTotal?.toFixed(2) || '0.00'}
                                    </p>
                                    <p className="text-xs text-gray-500">(İndirim: ₺{calculationData.flashSaleCalculation?.flashSaleDiscount?.toFixed(2) || '0.00'})</p>
                                </div>
                                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                                    <p className="text-sm text-gray-600">İade Tutarı</p>
                                    <p className="text-2xl font-bold text-green-600">
                                        ₺{calculationData.estimatedRefundAmount?.toFixed(2) || '0.00'}
                                    </p>
                                    <p className="text-xs text-gray-500">(Ödediğiniz Tutar)</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                        ) : isCouponCampaign ? (
                <div className="space-y-4">
                    <div className="bg-white rounded-lg p-4 border border-purple-300">
                        <div className="text-center">
                            <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                                <span className="text-2xl">🎟️</span>
                            </div>
                            <h5 className="font-semibold text-gray-800 mb-2">Kupon İndirimi İadesi</h5>
                            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-4 text-left">
                                <p className="text-sm text-purple-800">
                                    <span className="font-semibold">Kupon indirimi</span> ile satın aldığınız ürünü iade ediyorsunuz.
                                    Ürün için ödediğiniz tutar iade edilecektir. Kupon indiriminiz siparişteki ürün oranına göre düşülmüştür.
                                </p>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="bg-blue-50 rounded-lg p-3">
                                    <p className="text-sm text-gray-600">Normal Fiyat</p>
                                    <p className="text-xl font-bold text-blue-600">
                                        ₺{calculationData.couponCalculation?.originalTotal?.toFixed(2) || '0.00'}
                                    </p>
                                </div>
                                <div className="bg-purple-50 rounded-lg p-3">
                                    <p className="text-sm text-gray-600">Kupon İndirimi</p>
                                    <p className="text-xl font-bold text-purple-600">
                                        -₺{calculationData.couponCalculation?.refundedDiscount?.toFixed(2) || '0.00'}
                                    </p>
                                </div>
                                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                                    <p className="text-sm text-gray-600">İade Tutarı</p>
                                    <p className="text-2xl font-bold text-green-600">
                                        ₺{calculationData.estimatedRefundAmount?.toFixed(2) || '0.00'}
                                    </p>
                                    <p className="text-xs text-gray-500">(Ödediğiniz Tutar)</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ) : hasCampaignImpact ? (
                <div className="space-y-4">
                    {/* Kampanya Bilgileri */}
                    <div className="bg-white rounded-lg p-4 border border-blue-200">
                        <h5 className="font-semibold text-gray-800 mb-3 flex items-center">
                            <span className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs mr-2">🏷️</span>
                            Etkilenen Kampanyalar
                        </h5>
                        <div className="space-y-3">
                            {refundCalculations.campaignImpact.map((campaign, index) => (
                                <motion.div 
                                    key={index}
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: index * 0.1 }}
                                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                                >
                                    <div className="flex items-center">
                                        <span className={`px-2 py-1 rounded-full text-xs font-medium mr-3 ${getCampaignBadgeColor(campaign.campaign_type)}`}>
                                            {getCampaignTypeDisplayName(campaign.campaign_type)}
                                        </span>
                                        <div>
                                            <p className="font-medium text-gray-800">{campaign.campaign_name || 'Kampanya'}</p>
                                            <p className="text-sm text-gray-600">
                                                Orijinal İndirim: ₺{campaign.originalDiscount?.toFixed(2) || '0.00'}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <p className="font-semibold text-orange-600">
                                            -₺{campaign.refundedAmount?.toFixed(2) || '0.00'}
                                        </p>
                                        <p className="text-xs text-gray-500">İade Etkisi</p>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>

                    {/* Hesaplama Özeti */}
                    <div className="bg-white rounded-lg p-4 border border-blue-200">
                        <h5 className="font-semibold text-gray-800 mb-3 flex items-center">
                            <span className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center text-white text-xs mr-2">📊</span>
                            İade Hesap Özeti
                        </h5>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="text-center p-3 bg-blue-50 rounded-lg">
                                <p className="text-sm text-gray-600">Ürün Tutarı</p>
                                <p className="text-xl font-bold text-blue-600">
                                    ₺{refundCalculations.totalRefundAmount?.toFixed(2) || '0.00'}
                                </p>
                            </div>
                            <div className="text-center p-3 bg-orange-50 rounded-lg">
                                <p className="text-sm text-gray-600">Kampanya Etkisi</p>
                                <p className="text-xl font-bold text-orange-600">
                                    -₺{refundCalculations.campaignImpact.reduce((sum, c) => sum + (c.refundedAmount || 0), 0).toFixed(2)}
                                </p>
                            </div>
                            <div className="text-center p-3 bg-green-50 rounded-lg border-2 border-green-200">
                                <p className="text-sm text-gray-600">Tahmini İade Tutarı</p>
                                <p className="text-2xl font-bold text-green-600">
                                    ₺{calculationData.estimatedRefundAmount?.toFixed(2) || '0.00'}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Bilgilendirme */}
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                        <div className="flex items-start">
                            <div className="w-6 h-6 bg-yellow-500 rounded-full flex items-center justify-center mr-3 mt-0.5">
                                <span className="text-white text-xs">ℹ️</span>
                            </div>
                            <div>
                                <p className="text-sm font-semibold text-yellow-800 mb-1">Önemli Bilgi</p>
                                <p className="text-sm text-yellow-700">
                                    İade tutarı, sipariş sırasında kullanılan kampanya indirimlerinin orantılı olarak 
                                    düşülmesi ile hesaplanmıştır. Nihai tutar, iade onayı sonrasında kesinleşecektir.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                // Kampanya yok veya etki yok ise basit hesaplama göster
                <div className="space-y-4">
                    <div className="bg-white rounded-lg p-4 border border-blue-200">
                        <div className="text-center">
                            {!orderHasCampaign ? (
                                <>
                                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                                        <span className="text-2xl">💰</span>
                                    </div>
                                    <h5 className="font-semibold text-gray-800 mb-2">Normal İade İşlemi</h5>
                                    <p className="text-sm text-gray-600 mb-4">
                                        Bu siparişte kampanya indirimi bulunmadığı için normal iade işlemi uygulanacaktır.
                                    </p>
                                </>
                            ) : (
                                <>
                                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                                        <span className="text-2xl">📋</span>
                                    </div>
                                    <h5 className="font-semibold text-gray-800 mb-2">Kampanya Etkisi Bulunmuyor</h5>
                                    <p className="text-sm text-gray-600 mb-4">
                                        Siparişte kampanya kullanılmış ancak seçtiğiniz ürünler için kampanya etkisi bulunmuyor.
                                    </p>
                                </>
                            )}
                            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                <p className="text-sm text-gray-600">Tahmini İade Tutarı</p>
                                <p className="text-2xl font-bold text-green-600">
                                    ₺{calculationData.estimatedRefundAmount?.toFixed(2) || '0.00'}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </motion.div>
    );
};

export default CampaignRefundCalculation;